import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../../config/AuthContext";
import { Button, Card, FloatButton, message, Popover, Space, Switch, Table, Tag, Tooltip, Typography, Alert } from "antd"; // Added Alert
import { ColumnsType } from "antd/es/table";
import { GetProjecteId } from "../../../../dao/abp/GetProjecteId";
import { GetAlumnesTasquesByProject } from "../../../../dao/abp/GetAlumnesTasquesByProject";
import { PostProjectesIdAlumnesIdValidacio } from "../../../../dao/abp/PostProjectesIdAlumnesIdValidacio";
import { PostProjecteIdAlumneIdAvaluacio } from "../../../../dao/abp/PostProjecteIdAlumneIdAvaluacio";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import { BiComment, BiRefresh } from "react-icons/bi";
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Title, Text } = Typography;

interface Props {
    sprint: any;
}

const ProjecteIdTasques = (props: Props) => {
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const { id_grup, id_projecte } = useParams();

    const [modules, setModules] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [projecte, setProjecte] = useState<any>();

    useEffect(() => {
        if (isLoggedIn) {
            checkDades();
        }
    }, [id_projecte, props.sprint]);

    async function checkDades() {
        if (!id_projecte) return;
        checkProjecte(id_projecte);
        checkTasques();
    }

    async function checkProjecte(id: any) {
        setLoading(true);
        const data = await GetProjecteId(id + "", 0);

        const breadcrumbs = [
            { nom: 'Home', nav: '/home' },
            { nom: 'Avaluació', nav: '/abp' },
        ];

        if (user.tipus === "0") {
            breadcrumbs.push({ nom: `${data.nom}`, nav: `/abp/projectes/${data.id}` });
        } else {
            breadcrumbs.push(
                { nom: 'Projectes', nav: '/abp/projectes' },
                { nom: `${data.nom}`, nav: `/abp/projectes/${data.id}` },
                { nom: `${props.sprint.label}`, nav: `/abp/projectes/${data.id}/${props.sprint.id}` }
            );
        }
        setBreadCrumb(breadcrumbs, true);
        setProjecte(data);
        setLoading(false);
    }

    async function postSwitch(ele: any, valor: any) {
        setLoading(true);
        await PostProjectesIdAlumnesIdValidacio(id_grup + "", id_projecte + "", ele.id, valor);
        await checkTasques(); // Refresh to show updated state
        setLoading(false);
    }

    async function handleAvaluacioChange(studentId: string, modul: string, ra: string, val: number) {
        setLoading(true);
        await PostProjecteIdAlumneIdAvaluacio(id_projecte + "", studentId, modul, ra, val);
        message.success("Nota guardada correctament");
        await checkTasques();
        setLoading(false);
    }
    async function checkTasques() {
        setLoading(true);
        // Fetch ALL tasks (sprint 0) to ensure we have the full hierarchy
        const dat = await GetAlumnesTasquesByProject(id_grup + "", id_projecte ? id_projecte : "0", 0);
        setModules(dat);
        setLoading(false);
    }

    const buildTree = (tasks: any[]) => {
        if (!tasks) return [];
        const taskMap: Record<number, any> = {};
        const roots: any[] = [];

        tasks.forEach(t => {
            taskMap[t.id] = { ...t, children: [] };
        });

        tasks.forEach(t => {
            if (t.id_tarea_padre) {
                if (taskMap[t.id_tarea_padre]) {
                    taskMap[t.id_tarea_padre].children.push(taskMap[t.id]);
                } else {
                    roots.push(taskMap[t.id]);
                }
            } else {
                roots.push(taskMap[t.id]);
            }
        });

        const sortAndFlatten = (node: any, depth: number, result: any[]) => {
            const { children, ...nodeWithoutChildren } = node;
            nodeWithoutChildren.depth = depth;
            result.push(nodeWithoutChildren);

            if (children && children.length > 0) {
                children.sort((a: any, b: any) => a.ordre - b.ordre);
                children.forEach((child: any) => sortAndFlatten(child, depth + 1, result));
            }
        };

        roots.sort((a: any, b: any) => a.ordre - b.ordre);
        const flatList: any[] = [];
        roots.forEach(root => sortAndFlatten(root, 0, flatList));
        return flatList;
    };

    const filteredModules = React.useMemo(() => {
        if (!modules || modules.length === 0) return [];
        if (!props.sprint?.numero) return buildTree(modules);

        const fullTree = buildTree(modules);
        const currentSprintNumero = Number(props.sprint.numero);
        
        const tasksInSprint = fullTree.filter(m => Number(m.sprint) === currentSprintNumero);
        const ancestorIds = new Set<number>();
        
        tasksInSprint.forEach(task => {
            let parentId = task.id_tarea_padre;
            while (parentId) {
                ancestorIds.add(parentId);
                const parent = modules.find((m: any) => m.id === parentId);
                parentId = parent?.id_tarea_padre;
            }
        });

        return fullTree.filter(m => Number(m.sprint) === currentSprintNumero || ancestorIds.has(m.id));
    }, [modules, props.sprint]);

    // --- Configuración de Opciones de Nota ---
    const gradeOptions = [
        { val: 0, label: 'P', full: 'Pendent (0)', color: 'default' },
        { val: 1, label: 'NP', full: 'No Presentat (1)', color: 'volcanic' },
        { val: 2, label: 'M', full: 'Malament (2)', color: 'red' },
        { val: 5, label: 'S', full: 'Suficient (5)', color: 'orange' },
        { val: 6.5, label: 'B*', full: 'Bé * (6.5)', color: 'cyan' },
        { val: 7.5, label: 'B', full: 'Bé (7.5)', color: 'blue' },
        { val: 9, label: 'MB*', full: 'Molt Bé * (9)', color: 'purple' },
        { val: 10, label: 'MB', full: 'Molt Bé (10)', color: 'gold' },
    ];

    // --- Componente de Chips/Tags para Evaluar ---
    const GradeChips = ({ currentVal, onSelect }: { currentVal: number, onSelect: (v: number) => void }) => {
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {gradeOptions.map((opt) => {
                    const isSelected = currentVal === opt.val;
                    return (
                        <Tooltip title={opt.full} key={opt.val}>
                            <Tag
                                color={isSelected ? opt.color : 'default'}
                                style={{
                                    cursor: 'pointer',
                                    border: isSelected ? undefined : `1px solid #d9d9d9`,
                                    opacity: isSelected ? 1 : 0.7,
                                    fontWeight: isSelected ? 'bold' : 'normal',
                                    padding: '0 8px',
                                    margin: 0
                                }}
                                onClick={() => onSelect(opt.val)}
                            >
                                {opt.label}
                            </Tag>
                        </Tooltip>
                    )
                })}
            </div>
        );
    };

    // --- Columns for Students View (Read Only) ---
    const columnsAlumnos: ColumnsType<any> = [
        {
            title: 'Tasca / RA',
            key: 'ranom',
            width: 350,
            render: (_: any, ele: any) => (
                <div style={{ 
                    paddingLeft: ele.depth ? `${ele.depth * 30}px` : '10px', 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                }}>
                    <Space wrap align="center" size={4}>
                        {ele.depth > 0 && <Text type="secondary" style={{ fontSize: 18, marginRight: 4 }}>↳</Text>}
                        <Tag color="blue" style={{ fontSize: 11 }}>{ele.modul}</Tag>
                        <Tag color="cyan" style={{ fontSize: 11 }}>{ele.ranum || ele.ra}</Tag>
                        {ele.depth > 0 && <Tag color="purple" style={{ fontSize: 11 }}>Subtasca</Tag>}
                    </Space>
                    {ele.nom && <Text strong style={{ fontSize: 13, marginLeft: ele.depth > 0 ? 22 : 0 }}>{ele.nom}</Text>}
                </div>
            )
        },
        {
            title: 'Descripció',
            key: 'descripcio',
            dataIndex: 'descripcio',
            width: 400,
            render: (text: string) => (
                <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '13px' }}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ node, ...props }) => <p style={{ margin: 0 }} {...props} />
                        }}
                    >
                        {text}
                    </ReactMarkdown>
                </div>
            )
        },
        {
            title: 'Pes',
            key: 'pes',
            dataIndex: 'pes',
            width: 80,
            render: (val: any) => <Tag style={{ fontSize: 11 }}>{val}%</Tag>
        },
        {
            title: 'Obs.',
            key: 'observacions',
            dataIndex: 'observacions',
            width: 60,
            render: (obs: any) => obs ? (
                <Popover content={obs} title="Observacions">
                    <Button type="text" size="small" icon={<BiComment style={{ color: AvaluacioColors.colors.color5 }} />} />
                </Popover>
            ) : '-'
        },
        {
            title: 'Estat',
            key: 'fet',
            width: 80,
            align: 'center',
            render: (_: any, ele: any) => (
                <Switch
                    size="small"
                    checkedChildren={<CheckCircleOutlined />}
                    unCheckedChildren={<CloseCircleOutlined />}
                    checked={ele.progresAlumne === 1}
                    onChange={() => postSwitch(ele, ele.progresAlumne === 1 ? 0 : 1)}
                />
            )
        },
        {
            title: 'Nota',
            key: 'nota',
            width: 100,
            align: 'center',
            render: (_: any, ele: any) => {
                const foundItem = ele.grup ? ele.grup.find((item: any) => parseInt(item.id_usuari) === parseInt(userId)) : null;
                const notaVal = foundItem?.nota;
                const notaObj = gradeOptions.find(g => g.val === notaVal);

                return notaObj ? (
                    <Tag color={notaObj.color} style={{ fontSize: '12px', padding: '2px 8px' }}>
                        {notaObj.label}
                    </Tag>
                ) : <Tag style={{ fontSize: 11 }}>-</Tag>;
            }
        }
    ];

    // --- Columns for Teachers View (Editable) ---
    const columnsProfe: ColumnsType<any> = [
        {
            title: 'Info Tasca',
            key: 'info',
            width: '40%',
            render: (_: any, ele: any) => (
                <div style={{ 
                    paddingLeft: ele.depth ? `${ele.depth * 30}px` : '10px', 
                }}>
                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                        {ele.nom && <Text strong style={{ fontSize: 13, marginLeft: ele.depth > 0 ? 22 : 0 }}>{ele.nom}</Text>}
                        <Space wrap align="center">
                            {ele.depth > 0 && <Text type="secondary" style={{ fontSize: 18, marginRight: 4 }}>↳</Text>}
                            <Tag color="blue" style={{ fontSize: 11 }}>{ele.modul}</Tag>
                            <Tag color="geekblue" style={{ fontSize: 11 }}>{ele.ra}</Tag>
                            <Tag style={{ fontSize: 11 }}>{ele.pes}%</Tag>
                            {ele.depth > 0 && <Tag color="purple" style={{ fontSize: 11 }}>Subtasca</Tag>}
                        </Space>
                        <div style={{ fontSize: '13px', color: '#555', marginLeft: ele.depth > 0 ? 22 : 0 }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {ele.descripcio}
                            </ReactMarkdown>
                        </div>
                    </Space>
                </div>
            )
        },
        {
            title: 'Estudiants i Avaluació',
            key: 'actions',
            render: (_: any, ele: any) => {
                if (!ele.grup || ele.grup.length === 0) return <Text type="secondary" style={{ fontSize: 12 }}>Cap alumne asignat</Text>;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {ele.grup.map((studentItem: any) => (
                            <div key={studentItem.id_usuari} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 6 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <Text strong style={{ fontSize: 12 }}>{studentItem.nom || `Alumne ${studentItem.id_usuari}`}</Text>
                                    <Tag style={{ fontSize: 10 }}>ID: {studentItem.id_usuari}</Tag>
                                </div>
                                <GradeChips
                                    currentVal={parseFloat(studentItem.nota || 0)}
                                    onSelect={(val) => handleAvaluacioChange(studentItem.id_usuari, ele.modul, ele.ra, val)}
                                />
                            </div>
                        ))}
                    </div>
                );
            }
        },
    ];

    const isTeacher = user.tipus === "1" || user.tipus === "0";

    // --- Check if Sprint is Closed ---
    const now = new Date();
    const endDate = props.sprint?.data_final ? new Date(props.sprint.data_final) : null;
    let isClosed = false;
    if (endDate) {
        isClosed = now > endDate;
    }

    return (
        <Card
            title={<Title level={4} style={{ margin: 0 }}>Tasques del Sprint {props.sprint?.numero}: {props.sprint?.label}</Title>}
            bordered={false}
            className="shadow-sm"
            style={{ borderRadius: '12px' }}
        >
            {isClosed && (
                <Alert
                    message="Sprint Tancat"
                    description="ATENCIÓ: Aquest sprint ja ha finalitzat. Tota entrega realitzada a partir d'ara serà penalitzada per entrega tardana."
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}
            <Table
                rowKey="id"
                size="middle"
                loading={loading}
                bordered={false}
                pagination={false}
                columns={isTeacher ? columnsProfe : columnsAlumnos}
                dataSource={filteredModules}
                onRow={(record) => {
                    const isSubtask = record.depth > 0;
                    return {
                        style: {
                            background: isSubtask ? '#fcfcfc' : '#ffffff',
                            borderLeft: isSubtask ? `6px solid #babbbd` : '6px solid transparent'
                        }
                    };
                }}
                scroll={{ x: 800 }}
            />

            {!loading && (
                <FloatButton
                    shape="square"
                    type="primary"
                    onClick={() => checkDades()}
                    style={{ right: 24, bottom: 24 }}
                    icon={<BiRefresh />}
                    tooltip="Actualitzar dades"
                />
            )}
        </Card>
    );
};

export default ProjecteIdTasques;
